import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import AddCreditCardModal from './AddCreditCardModal';
import { CreditCardUsageModal, CreditCardPaymentModal, CreditCardTransactionsModal, CreditCardForAction } from './CreditCardActionModals';

interface CreditCard {
  id: number;
  companyId: string;
  bankAccountId?: number | null;
  branchId?: string | null;
  accountName: string;
  description?: string;
  cardNumber: string;
  statementDay: number;
  dueDay: number;
  creditLimit: number;
  minPaymentAmount: number;
  usedAmount: number;
  currency: string;
  isActive: boolean;
  isBranchOnly: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const CreditCards: React.FC = () => {
  const { t, i18n } = useTranslation('accounting');
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [cards, setCards] = useState<CreditCard[]>([]);
  const [bankNameMap, setBankNameMap] = useState<{ [id: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Satır bazlı 3 nokta menüsü
  const [anchorElRow, setAnchorElRow] = useState<{ [key: string]: HTMLElement | null }>({});

  // Modal state'leri
  const [isAddCreditCardModalOpen, setIsAddCreditCardModalOpen] = useState(false);
  const [creditCardToEdit, setCreditCardToEdit] = useState<CreditCard | null>(null);
  const [editBankAccountId, setEditBankAccountId] = useState<number | undefined>(undefined);
  const [editBankAccountName, setEditBankAccountName] = useState<string | undefined>(undefined);

  const [creditCardActionTarget, setCreditCardActionTarget] = useState<CreditCardForAction | null>(null);
  const [isCreditCardUsageModalOpen, setIsCreditCardUsageModalOpen] = useState(false);
  const [isCreditCardPaymentModalOpen, setIsCreditCardPaymentModalOpen] = useState(false);
  const [isCreditCardTxModalOpen, setIsCreditCardTxModalOpen] = useState(false);

  const getCurrencySymbol = (currency: string): string => {
    switch (currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return currency;
    }
  };

  const loadBankNames = useCallback(async () => {
    try {
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
      const result = response.data;
      const banks: any[] = result.data || result;
      const map: { [id: string]: string } = {};
      (banks || []).forEach((b: any) => {
        map[String(b.id)] = b.bankName || '';
      });
      setBankNameMap(map);
    } catch (err) {
      console.error('❌ Banka adları yüklenirken hata:', err);
    }
  }, []);

  const loadCards = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountingApi.get(ACCOUNTING_API.CREDIT_CARD_BY_COMPANY);
      const result = response.data;
      const list: CreditCard[] = result.data || result;
      setCards(Array.isArray(list) ? list : []);
    } catch (err: any) {
      console.error('❌ Kredi kartları yüklenirken hata:', err);
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userDetails?.companyId) {
      setCompanyId(userDetails.companyId);
      loadBankNames();
      loadCards();
    }
  }, [userDetails?.companyId, loadBankNames, loadCards]);

  // Modal yardımcıları
  const toCardForAction = (card: CreditCard): CreditCardForAction => ({
    id: card.id,
    accountName: card.accountName || '',
    cardNumber: card.cardNumber || '',
    creditLimit: Number(card.creditLimit) || 0,
    usedAmount: Number(card.usedAmount) || 0,
    currency: card.currency || 'TRY',
  });

  const handleOpenCreditCardUsage = (card: CreditCard) => {
    setCreditCardActionTarget(toCardForAction(card));
    setIsCreditCardUsageModalOpen(true);
  };

  const handleOpenCreditCardPayment = (card: CreditCard) => {
    setCreditCardActionTarget(toCardForAction(card));
    setIsCreditCardPaymentModalOpen(true);
  };

  const handleOpenCreditCardTransactions = (card: CreditCard) => {
    setCreditCardActionTarget(toCardForAction(card));
    setIsCreditCardTxModalOpen(true);
  };

  const handleEditCreditCard = (card: CreditCard) => {
    setCreditCardToEdit(card);
    setEditBankAccountId(card.bankAccountId ?? undefined);
    setEditBankAccountName(card.bankAccountId ? bankNameMap[String(card.bankAccountId)] : undefined);
    setIsAddCreditCardModalOpen(true);
  };

  const handleCloseAddCreditCardModal = () => {
    setIsAddCreditCardModalOpen(false);
    setCreditCardToEdit(null);
    setEditBankAccountId(undefined);
    setEditBankAccountName(undefined);
    loadCards();
  };

  const handleCreditCardActionSuccess = () => {
    loadCards();
  };

  const handleDeleteCreditCard = async (card: CreditCard) => {
    const confirmMsg = i18n.language.startsWith('tr')
      ? `"${card.accountName}" kredi kartını silmek istediğinize emin misiniz?`
      : `Are you sure you want to delete the credit card "${card.accountName}"?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await accountingApi.post(ACCOUNTING_API.CREDIT_CARD_DELETE(card.id), {}, {
        params: { deletedBy: userDetails?.email || 'Sistem' },
      });
      loadCards();
    } catch (err: any) {
      console.error('Kredi kartı silinemedi:', err);
      setError((i18n.language.startsWith('tr') ? 'Kredi kartı silinemedi: ' : 'Could not delete credit card: ') + (err?.response?.data?.message || err.message));
    }
  };

  // Arama filtresi
  const filteredCards = useMemo(() => {
    if (!searchTerm.trim()) return cards;
    const term = searchTerm.toLowerCase();
    return cards.filter((c) =>
      (c.accountName || '').toLowerCase().includes(term) ||
      (c.cardNumber || '').toLowerCase().includes(term) ||
      (c.bankAccountId ? (bankNameMap[String(c.bankAccountId)] || '') : '').toLowerCase().includes(term)
    );
  }, [cards, searchTerm, bankNameMap]);

  const columns = useMemo<DataTableColumn<CreditCard>[]>(() => [
    {
      id: 'index',
      label: t('bank.creditCardsPage.columns.row'),
      width: '60px',
      align: 'center',
      render: (_row, index) => <>{index + 1}</>,
    },
    {
      id: 'name',
      label: t('bank.creditCardsPage.columns.name'),
      field: 'accountName',
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditCardIcon fontSize="small" color="secondary" />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.accountName}</Typography>
            <Typography variant="caption" color="text.secondary">
              **** **** **** {(row.cardNumber || '').slice(-4)}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'bank',
      label: t('bank.creditCardsPage.columns.bank'),
      sortable: false,
      render: (row) => <>{row.bankAccountId ? (bankNameMap[String(row.bankAccountId)] || '-') : '-'}</>,
    },
    {
      id: 'currency',
      label: t('bank.table.columns.currency'),
      field: 'currency',
      sortable: true,
      render: (row) => <>{row.currency} ({getCurrencySymbol(row.currency)})</>,
    },
    {
      id: 'balance',
      label: t('bank.table.columns.balance'),
      align: 'right',
      render: (row) => (
        <Tooltip title={t('bank.labels.cardLimitTooltip', { value: `${getCurrencySymbol(row.currency)}${row.creditLimit.toLocaleString(locale)}` })}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
              {t('bank.labels.spent')}: {row.usedAmount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(row.currency)}
            </Typography>
            <Typography variant="caption" color="success.main">
              {t('bank.labels.availableLimit')}: {getCurrencySymbol(row.currency)}{Math.max(0, row.creditLimit - row.usedAmount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      id: 'accountType',
      label: t('bank.table.columns.accountType'),
      render: (row) => (
        <>
          {t('bank.labels.statementDay', { day: row.statementDay })}
          <br />
          {t('bank.labels.paymentDay', { day: row.dueDay })}
        </>
      ),
    },
    {
      id: 'dueDay',
      label: t('bank.creditCardsPage.columns.dueDay'),
      field: 'dueDay',
      sortable: true,
      align: 'center',
      render: (row) => <>{t('bank.creditCardsPage.dueDayValue', { day: row.dueDay })}</>,
    },
    {
      id: 'actions',
      label: t('bank.table.columns.actions'),
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorElRow(prev => ({ ...prev, [`credit-${row.id}`]: e.currentTarget })); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorElRow[`credit-${row.id}`]}
            open={Boolean(anchorElRow[`credit-${row.id}`])}
            onClose={() => setAnchorElRow(prev => ({ ...prev, [`credit-${row.id}`]: null }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${row.id}`]: null })); handleOpenCreditCardTransactions(row); }}>
              <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
              <ListItemText primary={t('bank.actions.transactions')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${row.id}`]: null })); handleEditCreditCard(row); }}>
              <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={t('bank.actions.edit')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${row.id}`]: null })); handleOpenCreditCardUsage(row); }}>
              <ListItemIcon><CreditCardIcon fontSize="small" color="info" /></ListItemIcon>
              <ListItemText primary={t('bank.actions.addExpense')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${row.id}`]: null })); handleOpenCreditCardPayment(row); }}>
              <ListItemIcon><AccountBalanceWalletIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary={t('bank.actions.makePayment')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${row.id}`]: null })); handleDeleteCreditCard(row); }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary={t('bank.actions.delete')} />
            </MenuItem>
          </Menu>
        </Box>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [anchorElRow, bankNameMap, locale, t]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Başlık */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/banka')} aria-label={t('bank.creditCardsPage.back')}>
          <ArrowBackIcon />
        </IconButton>
        <CreditCardIcon color="secondary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" component="h1" fontWeight="bold">
          {t('bank.creditCardsPage.title')}
        </Typography>
      </Box>

      {/* Arama + Yenile */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('bank.creditCardsPage.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadBankNames(); loadCards(); }}>
              {t('bank.actions.refresh')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <AccountingDataTable<CreditCard>
          columns={columns}
          rows={filteredCards}
          getRowId={(row) => `credit-card-${row.id}`}
          loading={loading}
          defaultRowsPerPage={25}
          emptyMessage={searchTerm ? t('bank.creditCardsPage.empty.search') : t('bank.creditCardsPage.empty.initial')}
          emptyIcon={<CreditCardIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
          ariaLabel={t('bank.creditCardsPage.tableAriaLabel')}
          columnResizeStorageKey="credit-cards"
        />
      )}

      {/* Modallar */}
      <AddCreditCardModal
        open={isAddCreditCardModalOpen}
        onClose={handleCloseAddCreditCardModal}
        bankAccountId={editBankAccountId}
        bankAccountName={editBankAccountName}
        cardToEdit={creditCardToEdit as any}
      />

      <CreditCardUsageModal
        open={isCreditCardUsageModalOpen}
        onClose={() => setIsCreditCardUsageModalOpen(false)}
        card={creditCardActionTarget}
        onSuccess={handleCreditCardActionSuccess}
      />

      <CreditCardPaymentModal
        open={isCreditCardPaymentModalOpen}
        onClose={() => setIsCreditCardPaymentModalOpen(false)}
        card={creditCardActionTarget}
        onSuccess={handleCreditCardActionSuccess}
      />

      <CreditCardTransactionsModal
        open={isCreditCardTxModalOpen}
        onClose={() => setIsCreditCardTxModalOpen(false)}
        card={creditCardActionTarget}
        onChanged={handleCreditCardActionSuccess}
      />
    </Box>
  );
};

export default CreditCards;
